import React from 'react'
import {
  BaseForm,
  Input,
  Searchable,
  VALIDATION_TYPES,
} from '../../../../components/Form'
import API from 'lib/api'
import { getMessage } from '../../../../lib/translator'

import EndpointMatrix from './EndpointMatrix'

const ALL_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']

function allowedList(endpoint) {
  const allowed = endpoint && endpoint.allowedMethods
  if (Array.isArray(allowed)) {
    return allowed
  }
  return Object.keys(allowed || {}).filter((m) => allowed[m])
}

export default class RolesForm extends BaseForm {
  constructor(props) {
    super(props)
    this.state = Object.assign(this.state, { catalogById: {} })
    this.onToggleMethod = this.onToggleMethod.bind(this)
    this.onEndpointsChange = this.onEndpointsChange.bind(this)
  }

  componentDidMount() {
    // Load the endpoint catalog once so the matrix knows each endpoint's allowed methods.
    this.catalogApi = new API({ url: '/account-service/endpoint' })
    this.catalogApi.get({ paginated: 'false' }).then(
      (res) => {
        const list = (res && res.data && res.data.endpoint) || []
        const catalogById = {}
        list.forEach((e) => {
          catalogById[e.id] = {
            id: e.id,
            url: e.url,
            allowedMethods: allowedList(e),
          }
        })
        this.setState({ catalogById })
        const id = this.state.values && this.state.values.id
        if (id) {
          this.loadRoleDetail(id, catalogById)
        }
      },
      () => {}
    )
  }

  componentWillUnmount() {
    this.catalogApi && this.catalogApi.cancel && this.catalogApi.cancel()
    this.roleApi && this.roleApi.cancel && this.roleApi.cancel()
  }

  // ListingPage seeds the edit form from the list row ({id,name}) only — it does not fetch
  // GET /role/{id}. So fetch the role's attached endpoints here. The single-role response is
  // the Detail object at response.data ({id,name,endpoints:[{id,url,method:[]}]}).
  loadRoleDetail(id, catalogById) {
    this.roleApi = new API({ url: `/account-service/role/${id}` })
    this.roleApi.get().then(
      (res) => {
        const detail = (res && res.data) || {}
        const attached = detail.endpoints || []
        const endpoints = attached.map(
          (e) =>
            catalogById[e.id] || {
              id: e.id,
              url: e.url,
              allowedMethods: e.method || [],
            }
        )
        const methods = {}
        attached.forEach((e) => {
          methods[e.id] = e.method || []
        })
        this.updateState(['endpoints'], endpoints, false)
        this.updateState(['methods'], methods, false)
      },
      () => {}
    )
  }

  // Default a newly-selected endpoint to all of its allowed methods.
  onEndpointsChange(selected) {
    const methods = Object.assign({}, this.getState(['methods']) || {})
    ;(selected || []).forEach((ep) => {
      if (methods[ep.id] === undefined) {
        const cat = this.state.catalogById[ep.id]
        methods[ep.id] = cat ? cat.allowedMethods.slice() : allowedList(ep)
      }
    })
    this.updateState(['methods'], methods, false)
  }

  onToggleMethod(endpointId, method, checked) {
    const current = new Set(
      this.getState(['methods', String(endpointId)]) || []
    )
    if (checked) {
      current.add(method)
    } else {
      current.delete(method)
    }
    const next = ALL_METHODS.filter((m) => current.has(m))
    this.updateState(['methods', String(endpointId)], next, false)
  }

  render() {
    const { Form } = this.components
    const { SubmitButton, CancelButton } = this.buttons
    const selected = this.getState(['endpoints']) || []
    // Enrich with catalog entries so the matrix has allowedMethods (array) for each endpoint.
    const enriched = selected.map((ep) => this.state.catalogById[ep.id] || ep)
    const methods = this.getState(['methods']) || {}
    return (
      <Form className="roles-form">
        <Input
          type="text"
          label={getMessage('role.name')}
          placeholder={getMessage('role.name.placeholder')}
          name="name"
          required
          {...this.generateStateMappers({
            stateKeys: ['name'],
            validationType: VALIDATION_TYPES.ONSUBMIT,
            loseEmphasisOnFill: true,
          })}
        />
        <Searchable
          label={getMessage('role.form.endpoints')}
          placeholder={getMessage('role.form.endpoints.placeholder')}
          name="endpoints"
          type="text"
          searchUrl="/account-service/endpoint"
          searchKey="url"
          valueKey="id"
          nameKey="url"
          multiple
          transformResponse={(response) => response.data.endpoint}
          {...this.generateStateMappers({
            stateKeys: ['endpoints'],
            loseEmphasisOnFill: true,
            onChange: this.onEndpointsChange,
          })}
        />
        <EndpointMatrix
          endpoints={enriched}
          methods={methods}
          onToggle={this.onToggleMethod}
        />
        <div className="form-actions">
          <CancelButton>{getMessage('role.cancel')}</CancelButton>
          <SubmitButton>{getMessage('role.submit')}</SubmitButton>
        </div>
      </Form>
    )
  }
}
